// src/pages/admin/AdminLayout.tsx
//
// Admin section layout. Sidebar nav + content area.
//
// Per docs/operations/iam-model.md the admin section is gated
// by role, not just by auth state. The five admin-eligible
// account types see different nav entries:
//   - admin:              everything (incl. settings, users,
//                          incentives)
//   - demo:               everything except settings/users/
//                          incentive writeback (read-mostly)
//   - auditor:            everything except settings/users/
//                          sales-writeback (read-only + audit log)
//   - marketing-sales:    dashboard + campaigns + customers (R)
//                          + FileMaker sync (R)
//   - sales:              dashboard + sales (own) + customers (R)
//                          + FileMaker sync (R)

import {
  NavLink,
  Navigate,
  Outlet,
  matchRoutes,
  useLocation,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  ShieldCheck,
  Package,
  ClipboardList,
  Users,
  Receipt,
  Truck,
  Wrench,
  FileText,
  PackageOpen,
  Megaphone,
  Database,
  Settings,
  TrendingUp,
  Target,
  Tag,
  FolderTree,
  ChevronRight,
  Barcode,
  Laptop,
} from "lucide-react";
import { useAuth } from "@/components/auth/AuthContext";
import { BenchmarkWatermark } from "@/components/layout/BenchmarkWatermark";
import { cn } from "@/lib/cn";
import { isFeatureEnabled, type FeatureFlag } from "@/lib/featureFlags";
import { isAdminSideEligible, type AccountType } from "@/lib/auth";
import {
  inventoryAccessMessages,
  inventoryCapabilities,
} from "@/lib/inventory/capabilities";

interface NavItem {
  to: string;
  /**
   * i18n key for the nav label (e.g. "admin.nav.dashboard"). The label is
   * resolved with t() at render time, so the nav follows the visitor's
   * language like the rest of the public UI.
   */
  labelKey: string;
  icon: typeof LayoutDashboard;
  /** Account types that can see this entry. Empty array = everyone admin-eligible. */
  visibleTo: AccountType[];
  /**
   * Fail-closed feature flag (M2 A21). When set, the entry is hidden
   * from the nav unless the flag is explicitly enabled at build time.
   */
  flag?: FeatureFlag;
}

const navItems: NavItem[] = [
  { to: "/admin", labelKey: "admin.nav.dashboard", icon: LayoutDashboard, visibleTo: [] },
  {
    to: "/admin/approvals",
    labelKey: "admin.nav.approvals",
    icon: ShieldCheck,
    visibleTo: [],
    flag: "adminApprovals",
  },
  {
    to: "/admin/products",
    labelKey: "admin.nav.products",
    icon: Package,
    visibleTo: [],
    flag: "adminProducts",
  },
  {
    to: "/admin/orders",
    labelKey: "admin.nav.orderQueue",
    icon: ClipboardList,
    visibleTo: [],
  },
  { to: "/admin/invoices", labelKey: "admin.nav.invoices", icon: Receipt, visibleTo: [] },
  { to: "/admin/customers", labelKey: "admin.nav.customers", icon: Users, visibleTo: [] },
  // Brand directory: shows every brand in the ledger with a
  // placeholder logo + product count, and a "Hide from catalog"
  // toggle per brand. Backend is the source of truth for hide state
  // (see shopdb/visibility.py + /api/admin/visibility/hide). The
  // nav entry mirrors the public /catalog Brands view so admins
  // can manage what the public sees.
  { to: "/admin/brands", labelKey: "admin.nav.brands", icon: Tag, visibleTo: [] },
  {
    to: "/admin/categories",
    labelKey: "admin.nav.categories",
    icon: FolderTree,
    visibleTo: [],
  },
  {
    to: "/admin/sales",
    labelKey: "admin.nav.sales",
    icon: TrendingUp,
    visibleTo: ["admin", "demo", "sales"],
  },
  {
    to: "/admin/incentives",
    labelKey: "admin.nav.incentives",
    icon: Target,
    visibleTo: ["admin", "demo", "sales"],
  },
  { to: "/admin/shipping", labelKey: "admin.nav.shipping", icon: Truck, visibleTo: [] },
  { to: "/admin/repairs", labelKey: "admin.nav.repairs", icon: Wrench, visibleTo: [] },
  {
    to: "/admin/quotations",
    labelKey: "admin.nav.quotations",
    icon: FileText,
    visibleTo: [],
  },
  {
    to: "/admin/lending",
    labelKey: "admin.nav.lending",
    icon: PackageOpen,
    visibleTo: [],
  },
  {
    to: "/admin/campaigns",
    labelKey: "admin.nav.campaigns",
    icon: Megaphone,
    visibleTo: [],
    flag: "adminCampaigns",
  },
  {
    to: "/admin/filemaker-sync",
    labelKey: "admin.nav.fmSync",
    icon: Database,
    visibleTo: [],
    flag: "adminFilemakerSync",
  },
  { to: "/admin/imports", labelKey: "admin.nav.fmImports", icon: Database, visibleTo: [] },
  {
    to: "/admin/fm-sync-client",
    labelKey: "admin.nav.fmSyncClient",
    icon: Laptop,
    visibleTo: [],
    flag: "adminFmSyncClient",
  },
  { to: "/admin/exports", labelKey: "admin.nav.fmExports", icon: Database, visibleTo: [] },
  {
    to: "/admin/inventory",
    labelKey: "admin.nav.inventory",
    icon: Barcode,
    // Inventory uses explicit session-group capabilities, not global account types.
    visibleTo: [],
  },
  {
    to: "/admin/users",
    labelKey: "admin.nav.usersRoles",
    icon: Users,
    visibleTo: ["admin", "demo"],
    flag: "adminUsers",
  },
  {
    to: "/admin/settings",
    labelKey: "admin.nav.settings",
    icon: Settings,
    visibleTo: ["admin"],
  },
];

function isVisible(item: NavItem, accountType: AccountType): boolean {
  if (item.flag && !isFeatureEnabled(item.flag)) return false;
  if (item.visibleTo.length === 0) return true;
  return item.visibleTo.includes(accountType);
}

/**
 * i18n key per account type, for the role badge in the admin sidebar.
 * The whole phrase is one key so the word order can differ per language.
 */
const ROLE_LABEL_KEY: Record<AccountType, string> = {
  anonymous: "admin.role.anonymous",
  demo: "admin.role.demo",
  admin: "admin.role.admin",
  customer: "admin.role.customer",
  auditor: "admin.role.auditor",
  "marketing-sales": "admin.role.marketing",
  sales: "admin.role.sales",
  legal: "admin.role.legal",
  management: "admin.role.management",
};

/**
 * Admin sidebar and pre-mount access boundary. Inventory roles do not alter the
 * global AccountType mapping: a warehouse-only session gets only inventory.
 * All API authorization remains enforced by the backend.
 */
export function AdminLayout() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const copy = inventoryAccessMessages(i18n.resolvedLanguage ?? i18n.language);

  // A protected child may fetch immediately on mount: wait for session validation.
  if (!auth.isReady) {
    return (
      <div role="status" className="container py-12 text-center">
        {copy.loading}
      </div>
    );
  }

  // Anonymous -> /login, but carry the current path so the
  // Login page can bounce back here after sign-in. Without this,
  // an anonymous user clicking "Open" on a row gets sent to /login
  // and lands on /admin (not the row they wanted) - which looks
  // like a 404.
  if (!auth.isAuthenticated) {
    const from = location.pathname + location.search;
    const loginSearch = `?from=${encodeURIComponent(from)}`;
    return <Navigate to={`/login${loginSearch}`} replace />;
  }

  const capabilities = inventoryCapabilities(auth.groups);
  const adminEligible = isAdminSideEligible(auth.accountType);
  // bench-staff remains the legacy demo fallback. Adding that generic group to
  // a warehouse identity must not unlock financial/admin pages.
  const inventoryOnly =
    capabilities.read &&
    (auth.accountType === "anonymous" || auth.accountType === "demo");

  if (auth.accountType === "customer" || (!adminEligible && !inventoryOnly)) {
    return (
      <AccessRestricted
        title={copy.denied}
        message={
          auth.accountType === "customer"
            ? copy.customerDenied
            : copy.unidentifiedDenied
        }
      />
    );
  }

  // Use the router's own case/percent-decoding semantics. A pathname-prefix
  // check could let a demo subject mount /admin/%69nventory without capability.
  const inventoryRoute =
    matchRoutes([{ path: "/admin/inventory/*" }], location) !== null;
  const routeAllowed = inventoryRoute
    ? capabilities.read
    : adminEligible && !inventoryOnly;
  const visibleNav = navItems.filter((item) => {
    if (item.to === "/admin/inventory") return capabilities.read;
    return !inventoryOnly && isVisible(item, auth.accountType);
  });
  const roleLabel = inventoryOnly
    ? capabilities.supervise
      ? capabilities.receive
        ? copy.warehouseSupervisor
        : copy.supervisor
      : copy.warehouse
    : t(ROLE_LABEL_KEY[auth.accountType]);

  return (
    <>
      <BenchmarkWatermark intensity="subtle" />
      <div className="relative z-10 container grid gap-6 py-8 lg:grid-cols-[220px_1fr]">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-20 lg:h-[calc(100vh-6rem)]">
          <div className="mb-6">
            <h2 className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              {inventoryOnly ? copy.inventory : t("admin.nav.dashboard")}
            </h2>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm font-510 text-fg-secondary">
                {roleLabel}
              </span>
              <span className="rounded bg-bg-elevated px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-fg-tertiary">
                {inventoryOnly ? copy.inventory : auth.accountType}
              </span>
            </div>
          </div>

          <nav className="space-y-0.5">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/admin"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-benchmark-teal/15 text-benchmark-teal-bright"
                      : "text-fg-secondary hover:bg-bg-elevated hover:text-fg-primary",
                  )
                }
              >
                <span className="flex items-center gap-2">
                  <item.icon className="h-4 w-4" strokeWidth={1.5} />
                  {item.to === "/admin/inventory" ? copy.inventory : t(item.labelKey)}
                </span>
                <ChevronRight
                  className="h-3 w-3 opacity-60"
                  strokeWidth={1.5}
                />
              </NavLink>
            ))}
          </nav>

          {/* Pending approval count (quick access) — demo-backed, so
              only shown when the approvals surface is enabled. */}
          {!inventoryOnly && isFeatureEnabled("adminApprovals") && (
            <PendingApprovalsBadge />
          )}
        </aside>

        {/* Content area */}
        <main className="min-w-0">
          {routeAllowed ? (
            <Outlet />
          ) : (
            <AccessRestricted
              title={copy.denied}
              message={
                inventoryOnly ? copy.inventoryOnlyDenied : copy.inventoryDenied
              }
            />
          )}
        </main>
      </div>
    </>
  );
}

function AccessRestricted({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <div role="alert" className="container py-12 text-center">
      <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
        {title}
      </h1>
      <p className="mt-2 text-sm text-fg-tertiary">{message}</p>
    </div>
  );
}

function PendingApprovalsBadge() {
  const pending = 4;
  return (
    <div className="mt-6 rounded-md border border-dashed border-border bg-bg-panel/50 p-3 text-xs text-fg-quaternary">
      <div className="flex items-center justify-between">
        <span>{pending} items need your attention</span>
        <NavLink
          to="/admin/approvals"
          className="text-benchmark-teal-bright hover:underline"
        >
          Review →
        </NavLink>
      </div>
    </div>
  );
}
